import type { TFunction } from 'i18next'
import type { BadgeProps } from '@/components/ui/Badge'
import { intlLocale } from '@/i18n'
import type { PlacementDecision, PlacementStatus, RequestStatus, ResultSummary } from './types'

type Tone = NonNullable<BadgeProps['tone']>

export const DECISIONS: PlacementDecision[] = ['promote', 'repeat', 'leave', 'graduate']

export function decisionLabel(t: TFunction, d: PlacementDecision): string {
  switch (d) {
    case 'promote':
      return t('Promote')
    case 'repeat':
      return t('Repeat the year')
    case 'leave':
      return t('Leaves the school')
    case 'graduate':
      return t('Graduates')
  }
}

export function statusLabel(t: TFunction, s: PlacementStatus): string {
  switch (s) {
    case 'planned':
      return t('Planned')
    case 'confirmed':
      return t('Confirmed')
    case 'applied':
      return t('Applied')
    case 'cancelled':
      return t('Cancelled')
  }
}

export const statusTone: Record<PlacementStatus, Tone> = {
  planned: 'warning',
  confirmed: 'success',
  applied: 'brand',
  cancelled: 'neutral',
}

export const requestTone: Record<RequestStatus, Tone> = {
  none: 'neutral',
  requested: 'info',
  not_returning: 'danger',
}

/** "12,40/20" (or "62 %" without an out-of-N scale). */
export function formatScore(r: ResultSummary): string {
  const nf = new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (r.average !== null && r.outOf !== null) return `${nf.format(r.average)}/${r.outOf}`
  return `${new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1 }).format(r.percentage)} %`
}

/** Seats taken next year (enrolled + confirmed) and planned on top. */
export function seatsText(
  t: TFunction,
  s: { capacity: number | null; enrolled: number; planned: number; confirmed: number },
): string {
  const taken = s.enrolled + s.confirmed
  return s.capacity !== null
    ? t('{{taken}}/{{capacity}} seats · {{planned}} planned', {
        taken,
        capacity: s.capacity,
        planned: s.planned,
      })
    : t('{{taken}} seats · {{planned}} planned', { taken, planned: s.planned })
}
